/**
 * 支付方式面板品牌判定 —— `03-数据字典/70-产品配置.md` §3.7.2「同一策略组内面板品牌唯一」的前端同口径实现。
 *
 * 品牌键：目录行取 A8 §7.8.8 `method_key`，Stripe 同步行取 `stripe_type`（与 `method_key` 同一命名空间），
 * `method_key` 为空（`IAP` / 整个供应商收银台如 `PAYERMAX`）= 无品牌、不参与约束。
 * 钱包键按 App 平台判同品牌：IOS `wallet` ≡ `applepay`；ANDROID `wallet` ≡ `googlepay`；WEB `wallet` 分别与
 * `applepay`、`googlepay` 同品牌，但 `applepay` 与 `googlepay` 互不冲突。实现为「品牌令牌集合」：
 * `wallet` 展开成该平台的钱包令牌，其余键即自身；两个成员令牌集合有交集 = 同一面板品牌。
 *
 * 服务端 A8 §7.8.2 是权威（违反即 VALIDATION_FAILED），这里只把判定前移到候选与提交前提示。
 */

import type { PaymentMethodOption } from '@novel-admin/shared';

/** 品牌判定所需的 App 平台（`apps.platform` 闭集）；`null` = 平台未配置 */
export type PanelBrandPlatform = 'IOS' | 'ANDROID' | 'WEB';

/** App 平台（页面行展示值 `iOS` / `Android` / `WEB`，或 DTO 值）→ 品牌判定平台；其余返 null */
export function panelBrandPlatformOf(
  platform: string | null | undefined,
): PanelBrandPlatform | null {
  if (platform === 'iOS' || platform === 'IOS') return 'IOS';
  if (platform === 'Android' || platform === 'ANDROID') return 'ANDROID';
  if (platform === 'WEB') return 'WEB';
  return null;
}

const WALLET_KEY = 'wallet';

/** `wallet` 在各平台展开的钱包令牌。平台未知时按 WEB 取并集：只会多拦、不会漏拦，服务端按真实平台终判。 */
const WALLET_TOKENS: Record<PanelBrandPlatform | 'UNKNOWN', readonly string[]> = {
  IOS: ['applepay'],
  ANDROID: ['googlepay'],
  WEB: ['applepay', 'googlepay'],
  UNKNOWN: ['applepay', 'googlepay'],
};

/**
 * 可选项的面板品牌键：目录行 `method_key`，同步行 `stripe_type`；无品牌返 null。
 * 缺字段（Pages 已发、Worker 未发或回滚的版本错位，旧 §7.8.8 不带 `method_key`）同样归 null，
 * 降级为不参与约束（服务端为准），而不是让 `undefined` 把全部目录行判成同一品牌。
 */
export function panelBrandKeyOf(option: PaymentMethodOption): string | null {
  return (option.source === 'STRIPE_SYNC' ? option.stripe_type : option.method_key) ?? null;
}

/** 品牌键 → 品牌令牌集合（仅 `wallet` 按平台展开） */
export function panelBrandTokensOf(
  brandKey: string,
  platform: PanelBrandPlatform | null,
): readonly string[] {
  return brandKey === WALLET_KEY ? WALLET_TOKENS[platform ?? 'UNKNOWN'] : [brandKey];
}

/** 两个可选项是否同一面板品牌。任一无品牌 → 否；同一取值不算（那是「成员不得重复」另一条约束）。 */
export function isSamePanelBrand(
  a: PaymentMethodOption,
  b: PaymentMethodOption,
  platform: PanelBrandPlatform | null,
): boolean {
  if (a.payment_method === b.payment_method) return false;
  const keyA = panelBrandKeyOf(a);
  const keyB = panelBrandKeyOf(b);
  if (keyA === null || keyB === null) return false;
  const tokensB = panelBrandTokensOf(keyB, platform);
  return panelBrandTokensOf(keyA, platform).some((token) => tokensB.includes(token));
}

/**
 * 候选与已有成员中哪一个同品牌；无冲突返 null。
 * 不在可选列表的取值（已退役等）查不到品牌，不参与判定 —— 它们另由「已退役 / 不可选」提示拦。
 */
export function panelBrandConflictOf(
  candidate: string,
  members: readonly string[],
  optionOf: (method: string) => PaymentMethodOption | undefined,
  platform: PanelBrandPlatform | null,
): string | null {
  const candidateOption = optionOf(candidate);
  if (!candidateOption) return null;
  for (const member of members) {
    const memberOption = optionOf(member);
    if (memberOption && isSamePanelBrand(candidateOption, memberOption, platform)) return member;
  }
  return null;
}

/** 成员集合内的同品牌冲突：每个与更靠前成员同品牌的成员出一条 `{ earlier, member }` */
export function panelBrandConflictsIn(
  members: readonly string[],
  optionOf: (method: string) => PaymentMethodOption | undefined,
  platform: PanelBrandPlatform | null,
): { earlier: string; member: string }[] {
  const conflicts: { earlier: string; member: string }[] = [];
  members.forEach((member, index) => {
    const earlier = panelBrandConflictOf(member, members.slice(0, index), optionOf, platform);
    if (earlier !== null) conflicts.push({ earlier, member });
  });
  return conflicts;
}
